"use client";

// ── The `/judge` landing's community contests (`EX-PUB13`, `EX-PUB18`, `EX-PUB19`) ─
// Reads the same `/v1/contests?scope=upcoming` list `/contests` itself reads,
// but on its own query key: `contestKeys.directory` is keyed on page, not on
// this list's own `limit`, and sharing it would let the landing's fetch clobber
// the directory's 20-item cache (or the other way around).
//
// `EX-PUB18` (founder rule 2026-09-23): this is the community's list. A
// Progsity round, the `official` flag, is the "Next Progsity round" card
// beside it, so it is filtered out here, and every row carries a Community
// badge. `EX-PUB19` names the host on each row and moves the list onto the
// Arena's dark ground.

import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { ArrowRight, CalendarClock, CalendarX, Timer, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchPublicContests } from "@/features/contests/api";
import { HostContestLink } from "@/features/contests/components/HostContestLink";
import {
  contestHostLine,
  formatDateTime,
  formatDuration,
  formatRemaining,
  remainingUntil,
} from "@/features/contests/lib/format";
import { useContestDisplayZone } from "@/features/contests/lib/display-zone";
import { useNow } from "@/features/contests/lib/use-now";
import type { PublicContestSummary } from "@/features/contests/types";

const LIST_LIMIT = 3;
/** Enough of the directory that three community rows survive the Progsity rounds taken out of it. */
const FETCH_LIMIT = 20;

/** The community's contests in the directory's own order (soonest first), Progsity's rounds removed. */
export function communityContests(
  items: readonly PublicContestSummary[],
  limit = LIST_LIMIT,
): PublicContestSummary[] {
  return items.filter((contest) => !contest.official).slice(0, limit);
}

/**
 * One contest, as a full-width row: the title stays on one line and the date,
 * the countdown and the call to action sit beside it rather than under it
 * (the founder's 2026-09-10 note on the three-column version).
 */
function ContestStripRow({
  contest,
  locale,
  now,
}: {
  contest: PublicContestSummary;
  locale: string;
  now: number | null;
}) {
  const t = useTranslations("judge.home");
  const tContest = useTranslations("contest");
  const tDirectory = useTranslations("contest.directory");
  const zone = useContestDisplayZone();
  const title = locale === "bn" && contest.title_bn ? contest.title_bn : contest.title_en;
  const selfServe = contest.registration === "open" || contest.registration === "passphrase";
  const duration = formatDuration(contest.startAt, contest.endAt, locale);
  // To the start, and to the end once it has opened, as the directory's card counts.
  const remaining = now === null ? null : remainingUntil(contest.isOpen ? contest.endAt : contest.startAt, now);
  // `EX-HST03`: the organization in place of the owner when one hosts it.
  const hostName = contestHostLine(contest, locale)?.name ?? null;

  return (
    <Link
      href={`/${locale}/contests/${contest.slug}`}
      className="group flex w-full flex-col gap-3 rounded-xl border border-white/[0.08] bg-white/[0.03] p-4 transition-colors duration-200 ease-out hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-950"
    >
      <div className="flex min-w-0 flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <h4 className="min-w-0 text-[15px] font-bold tracking-tight text-white group-hover:text-accent-peach">
            {title}
          </h4>
          <span className="inline-flex shrink-0 items-center rounded-full border border-white/15 px-2 py-0.5 text-[11px] font-semibold text-neutral-300">
            {t("communityBadge")}
          </span>
          {contest.rated ? (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-violet-400/30 bg-violet-400/10 px-2 py-0.5 text-[11px] font-semibold text-violet-200">
              <Trophy className="h-3 w-3" aria-hidden />
              {tContest("rated")}
            </span>
          ) : null}
        </div>

        {hostName ? (
          <p className="text-[13px] text-neutral-400">
            {t("communityHostedBy")} <span className="font-medium text-neutral-200">{hostName}</span>
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1 text-xs text-neutral-300">
          <span className="inline-flex items-center gap-1.5">
            <CalendarClock className="h-3.5 w-3.5 shrink-0 text-neutral-500" aria-hidden />
            {formatDateTime(contest.startAt, locale, zone)}
          </span>
          {duration ? (
            <span className="inline-flex items-center gap-1.5">
              <Timer className="h-3.5 w-3.5 shrink-0 text-neutral-500" aria-hidden />
              {duration}
            </span>
          ) : null}
          <span>{tContest(`scoring.${contest.scoringFormat}`)}</span>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-white/[0.07] pt-3">
        {remaining && remaining.total > 0 ? (
          <span className="text-xs text-neutral-400">
            {contest.isOpen ? tDirectory("endsIn") : tDirectory("startsIn")}{" "}
            <span className="font-mono font-semibold tabular-nums text-white">{formatRemaining(remaining)}</span>
          </span>
        ) : (
          <span aria-hidden />
        )}
        <span className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-accent-peach">
          {selfServe ? t("upcomingRegister") : t("upcomingDetails")}
          <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 ease-out group-hover:translate-x-0.5" aria-hidden />
        </span>
      </div>
    </Link>
  );
}

/**
 * Designed state for a week with nothing scheduled: the quality bar `EX-PUB11`
 * set (and `EX-PUB13` carries forward) is an explained empty state, never a gap.
 */
function EmptyStrip({ locale }: { locale: string }) {
  const t = useTranslations("judge.home");
  return (
    <div className="flex w-full flex-col items-center gap-3 rounded-xl border border-dashed border-white/15 p-8 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/15">
        <CalendarX className="h-6 w-6 text-primary" aria-hidden />
      </span>
      <div>
        <p className="text-sm font-bold text-white">{t("upcomingEmptyTitle")}</p>
        <p className="mt-1 text-sm text-neutral-400">{t("upcomingEmptyBody")}</p>
      </div>
      <Button asChild variant="outlineDark" className="mt-1 h-11 px-4">
        <HostContestLink locale={locale}>{t("upcomingEmptyCta")}</HostContestLink>
      </Button>
    </div>
  );
}

export function JudgeUpcomingContests({ locale }: { locale: string }) {
  const t = useTranslations("judge.home");
  const now = useNow();
  const { data, isPending, isError } = useQuery({
    queryKey: ["judge-home", "upcoming-contests", FETCH_LIMIT],
    queryFn: () => fetchPublicContests({ scope: "upcoming", page: 1, limit: FETCH_LIMIT }),
    retry: false,
  });

  const items = communityContests(data?.items ?? []);

  /**
   * A stack, not a grid: one contest is one row, two are two, and there is no
   * column count to keep in sync with the limit. The skeleton is one row for
   * the same reason: promising three and delivering one is the layout shift,
   * and one is the common case.
   */
  return (
    <div className="flex flex-col gap-3">
      {isPending ? (
        <div className="h-[132px] w-full animate-pulse rounded-xl border border-white/[0.08] bg-white/[0.04]" aria-hidden />
      ) : isError ? (
        <p className="w-full rounded-xl border border-white/[0.08] p-8 text-center text-sm text-neutral-400">
          {t("upcomingLoadFailed")}
        </p>
      ) : items.length === 0 ? (
        <EmptyStrip locale={locale} />
      ) : (
        items.map((contest) => (
          <ContestStripRow key={contest._id} contest={contest} locale={locale} now={now} />
        ))
      )}
    </div>
  );
}
